import { Box, Button, Flex, MediaBox, MediaFlex } from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import { setExperiment } from "app-store-v2/app/actions"
import fetchProduct from "areas/product/hoc/fetch-product"
import withImages from "areas/product/hoc/with-images"
import ImageArea from "areas/product/image-area"
import { StyledImage } from "areas/product/jewlr/components"
import { trackSlide } from "areas/product/store/actions"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import { retry } from "helpers/application"
import { isVideoView, isJ3dView } from "helpers/gallery-slide-view"
import { useNavigation } from "helpers/use-navigation"
import fetchWishlist from "hoc/fetch-wishlist"

import ImageAreaContent from "./image-area-content"
import MainBody from "./main-body"
import Breadcrumbs from "./wizard-header/breadcrumbs"
import HeaderMobile from "./wizard-header/header-mobile"

const GenericNotFound = loadable(() =>
  retry(() => import("app/generic-not-found"))
)
const ProductFooter = loadable(() => retry(() => import("./product-footer")), {
  ssr: false,
})
const CartPopup = loadable(() => retry(() => import("areas/cart/popup")))
const CartPreviewModal = loadable(() =>
  retry(() => import("areas/cart/preview"))
)

if (typeof window != "undefined") {
  try {
    if (window.location.hash != "") {
      var hash = window.location.hash.split("#")[1]
      if (hash.match("&gid=[0-9]*&pid=[0-9]*")) {
        window.location.hash = ""
      }
    }
  } catch (e) {
    console.log(e)
  }

  // EDGE ONLY - remove this line once Microsoft Edge decides to start supporting
  // HTMLCanvasElement.toBlob
  try {
    if (
      !Object.prototype.hasOwnProperty.call(
        HTMLCanvasElement.prototype,
        "toBlob"
      )
    ) {
      require("blueimp-canvas-to-blob")
    }
  } catch (e) {
    // Continue if this polyfill fails to load
    console.log("Couldn't load toBlob polyfill", e)
  }
}

const ThumbnailBtn = styled(Button).attrs({
  variant: "text",
})`
  border: 3px solid
    ${(props) => (props.selected ? props.theme.colors.grey[10] : "transparent")};
  display: block;
  position: relative;
  :hover {
    border-color: ${(props) => props.theme.colors.grey[5]};
  }
  :focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
  }
`

const Container = styled(Box).attrs({
  bg: { _: "grey.4", tablet: "white" },
  contain: true,
  mb: { tablet: "10px" },
  pt: { _: "12px", tablet: 4 },
})`
  ${(props) => props.theme.mediaQueries.tablet`
    margin-left: auto;
    margin-right: auto;
    max-width: 835px;
  `}
  ${(props) => props.theme.mediaQueries.desktop`
    max-width: 100%;
  `}
  ${(props) => props.theme.mediaQueries.widescreen`
    margin-left: 64px;
    margin-right: 64px;
  `}
  @media (min-width: 1568px) {
    max-width: 1440px;
    margin-left: auto;
    margin-right: auto;
  }
`
const Thumbnail = ({
  index,
  onClick,
  productTitle,
  selected,
  src,
  tabIndex,
  view,
}) => {
  const keyboardProps = useNavigation()

  return (
    <ThumbnailBtn
      aria-controls={`image-slider-tabpanel-${index + 1}-main`}
      aria-label={`slide-${index + 1}`}
      aria-selected={selected}
      onClick={onClick}
      role="tab"
      selected={selected}
      tabIndex={tabIndex}
      {...keyboardProps}
    >
      <StyledImage
        alt={productTitle}
        className={
          view === "tangiblee_view"
            ? sl.config.env == "staging"
              ? "js-tangiblee-cta-v2"
              : "js-tangiblee-cta"
            : ""
        }
        data-cy={`product-main-slider-thumbnail-img-${index}`}
        height="48px"
        lazyload={index > 0}
        mb="2px"
        ratio="48 / 48"
        src={src}
        width="48px"
      />
    </ThumbnailBtn>
  )
}

Thumbnail.propTypes = {
  index: PropTypes.number,
  onClick: PropTypes.func,
  productTitle: PropTypes.string,
  selected: PropTypes.bool,
  src: PropTypes.string,
  tabIndex: PropTypes.string,
  view: PropTypes.string,
}

/**
 * Product index
 * @param {object} data - default data from _react.html
 */
const InternalProduct = ({
  currentSlideIndex,
  defaultImage,
  j3dSlideIndex,
  pdpPricingExp,
  // pdpViewIn3DConvertExp,
  // pdpViewIn3DExp,
  productNotFound,
  productTitle,
  setExperiment,
  slidesArray,
  trackSlide,
  wizardLoading,
}) => {
  const wizardBodyRef = useRef(null)

  useEffect(() => {
    if (pdpPricingExp) {
      setExperiment({
        pdpPricing: pdpPricingExp,
      })
    }
  }, [pdpPricingExp])

  // useEffect(() => {
  //   if (sl.config.env !== "production") {
  //     if (pdpViewIn3DExp) {
  //       setExperiment({ pdpViewIn3D: pdpViewIn3DExp })
  //     }
  //     if (pdpViewIn3DConvertExp) {
  //       setExperiment({ pdpViewIn3D_convert: pdpViewIn3DConvertExp })
  //     }
  //   }
  // }, [pdpViewIn3DExp, pdpViewIn3DConvertExp])

  if (productNotFound) {
    return <GenericNotFound />
  }

  return (
    <>
      <Container>
        <HeaderMobile />
        <Flex
          alignItems={{ tablet: "flex-start" }}
          flexDirection={{ _: "column", tablet: "row" }}
          gutter={{ _: 0, tablet: 2 }}
        >
          <Box flex="1" mb={2} width={{ tablet: "50%" }}>
            <ImageArea
              defaultImage={defaultImage}
              endOffset={80}
              topOffset={30}
              wizardBodyRef={wizardLoading ? {} : wizardBodyRef}
            >
              <Flex gap="44px">
                <MediaFlex
                  aria-label="Product Images"
                  flexDirection="column"
                  gap="12px"
                  greaterThanOrEqual="desktop"
                  minWidth="52px"
                  role="tablist"
                >
                  <FocusScope>
                    {slidesArray?.length > 0 &&
                      slidesArray
                        .filter(({ view }) => !isJ3dView(view))
                        .map((slide, index) => (
                          <Thumbnail
                            index={index}
                            key={`mainthumb-${slide.view}`}
                            onClick={() => trackSlide(index)}
                            productTitle={productTitle}
                            selected={currentSlideIndex === index}
                            src={
                              isVideoView(slide.view)
                                ? require("images/product/vr/videoicon.png")
                                : slide.src
                            }
                            tabIndex={
                              currentSlideIndex === index ||
                              (currentSlideIndex === j3dSlideIndex &&
                                index === 0)
                                ? "0"
                                : "-1"
                            }
                            view={slide.view}
                          />
                        ))}
                  </FocusScope>
                </MediaFlex>
                <ImageAreaContent />
              </Flex>
            </ImageArea>
          </Box>
          <Box ref={wizardBodyRef} width={{ desktop: "480px", tablet: "50%" }}>
            <MediaBox greaterThanOrEqual="tablet">
              <Breadcrumbs />
            </MediaBox>
            <MainBody />
          </Box>
        </Flex>
      </Container>
      <ProductFooter />
      <SimpleDeferAfterLoadWrapper>
        <CartPopup />
        <CartPreviewModal />
      </SimpleDeferAfterLoadWrapper>
    </>
  )
}

InternalProduct.propTypes = {
  currentSlideIndex: PropTypes.number,
  defaultImage: PropTypes.object,
  j3dSlideIndex: PropTypes.number,
  pdpPricingExp: PropTypes.object,
  // pdpViewIn3DConvertExp: PropTypes.object,
  // pdpViewIn3DExp: PropTypes.object,
  productNotFound: PropTypes.bool,
  productTitle: PropTypes.string,
  setExperiment: PropTypes.func,
  slidesArray: PropTypes.array,
  trackSlide: PropTypes.func,
  wizardLoading: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    currentSlideIndex: state.previewImage.slideIndex,
    defaultImage: state.wizard.miniview.default_view,
    pdpPricingExp: state.experiment.pdpPricing,
    // pdpViewIn3DConvertExp: state.experiment.pdpViewIn3D_convert,
    // pdpViewIn3DExp: state.experiment.pdpViewIn3D,
    productNotFound: state.wizardUI.productNotFound,
    productTitle: state.wizard.title,
    wizardLoading: state.wizard.loading,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setExperiment: (experiment) => dispatch(setExperiment(experiment)),
    trackSlide: (index) => {
      dispatch(trackSlide(index))
    },
  }
}

const Product = fetchWishlist(
  connect(mapStateToProps, mapDispatchToProps)(InternalProduct)
)

export default fetchProduct(withImages(Product))
